import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { History, Info, RefreshCw, TriangleAlert } from 'lucide-react';
import { settingsAPI } from '../services/api';
import { PageHeader, LoadingState, ErrorState } from '../components';
import { Popconfirm, toast, cx } from '../../../ui';
import { getErrorMessage } from '../../../utils/apiError';
import { formatDate } from '../utils/helpers';
import { refreshSiteConfig } from '../../../configs/siteConfig';
import SettingControl from './settings/SettingControl';
import {
  CheckoutStrip,
  HoursPreview,
  PhonePeHelp,
  SearchPreview,
  TestEmail,
  UpiPreview,
} from './settings/SectionExtras';
import {
  SITE_SECTIONS,
  sameAsCurrent,
  sectionState,
  toPayload,
  validateField,
  valueOf,
} from './settings/settingsModel';
import './settings/settings.css';

const HISTORY = 'history';
const PAYMENT_SECTIONS = ['upi', 'phonepe', 'checkout'];

const hasEntries = obj => Object.keys(obj || {}).length > 0;

/** Extra panels shown under a section's fields (previews, test email, values to copy). */
const SectionExtra = ({ section, draft, dirty }) => {
  const v = key => valueOf(section, key, draft);
  switch (section.id) {
    case 'upi':
      return (
        <UpiPreview
          upiId={v('Payments:Providers:manual-upi:UpiId')}
          payeeName={v('Payments:Providers:manual-upi:PayeeName')}
        />
      );
    case 'phonepe':
      return <PhonePeHelp />;
    case 'email':
      return <TestEmail disabled={dirty} />;
    case 'hours':
      return (
        <HoursPreview
          days={v('Site:SupportDays')}
          openHour={v('Site:SupportOpenHour')}
          closeHour={v('Site:SupportCloseHour')}
        />
      );
    case 'seo':
      return (
        <SearchPreview
          title={v('Site:SeoTitle')}
          description={v('Site:SeoDescription')}
          siteUrl={v('Email:PublicSiteUrl')}
        />
      );
    default:
      return null;
  }
};

const SettingsNav = ({ groups, active, status, drafts, resets, onSelect }) => (
  <nav className="adm-set__nav" aria-label="Settings sections">
    {groups.map(([group, sections]) => (
      <div key={group} className="adm-set__nav-group">
        <div className="adm-set__nav-heading">{group}</div>
        {sections.map(section => {
          const state = sectionState(section, status);
          const edited =
            hasEntries(drafts[section.id]) ||
            (resets[section.id] || []).length > 0;
          return (
            <button
              key={section.id}
              type="button"
              className={cx(
                'adm-set__nav-link',
                active === section.id && 'is-active'
              )}
              aria-current={active === section.id ? 'page' : undefined}
              onClick={() => onSelect(section.id)}
            >
              <span className="adm-set__nav-label">{section.title}</span>
              {edited && (
                <span
                  className="adm-set__edited"
                  title="Unsaved changes"
                  aria-label="unsaved changes"
                />
              )}
              {state && (
                <span className="adm-set__state" data-tone={state.tone}>
                  {state.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    ))}
    <div className="adm-set__nav-group">
      <button
        type="button"
        className={cx('adm-set__nav-link', active === HISTORY && 'is-active')}
        aria-current={active === HISTORY ? 'page' : undefined}
        onClick={() => onSelect(HISTORY)}
      >
        <History aria-hidden="true" />
        <span className="adm-set__nav-label">Change history</span>
      </button>
    </div>
  </nav>
);

const ChangeHistory = ({ history, sections, onOpen }) => (
  <section className="adm-panel" aria-labelledby="set-history-title">
    <div className="adm-panel__head">
      <div>
        <h2 className="adm-panel__title" id="set-history-title">
          Change history
        </h2>
        <p className="adm-set__desc">
          The last 30 changes. Secret values are never shown, only whether they
          changed.
        </p>
      </div>
    </div>
    {history.length === 0 ? (
      <p className="adm-set__empty">
        Nothing has been changed from the admin panel yet.
      </p>
    ) : (
      <ol className="adm-set-history">
        {history.map(entry => (
          <li key={entry.id} className="adm-set-history__item">
            <div className="adm-set-history__head">
              {sections.some(s => s.id === entry.sectionId) ? (
                <button
                  type="button"
                  className="adm-set-link"
                  onClick={() => onOpen(entry.sectionId)}
                >
                  {entry.sectionTitle}
                </button>
              ) : (
                <strong>{entry.sectionTitle}</strong>
              )}
              <span className="adm-cell-sub">
                {formatDate(entry.at, 'datetime')} · {entry.by}
              </span>
            </div>
            <ul className="adm-set-history__changes">
              {entry.changes.map(change => (
                <li key={change.key}>
                  <span className="adm-set-history__label">{change.label}</span>{' '}
                  <span className="adm-set-history__from">{change.from}</span>
                  <span aria-hidden="true"> → </span>
                  <span className="sr-only"> changed to </span>
                  <span className="adm-set-history__to">{change.to}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    )}
  </section>
);

const Settings = () => {
  const [load, setLoad] = useState({ status: 'loading', error: null });
  const [data, setData] = useState({ sections: [], status: null, history: [] });
  const [drafts, setDrafts] = useState({}); // { sectionId: { key: value } }
  const [resets, setResets] = useState({}); // { sectionId: [key] }
  const [errors, setErrors] = useState({}); // { key: message }
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();
  const mainRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    setLoad({ status: 'loading', error: null });
    try {
      const response = await settingsAPI.getAll();
      setData(
        response.data?.data ?? { sections: [], status: null, history: [] }
      );
      setLoad({ status: 'ready', error: null });
    } catch (error) {
      setLoad({
        status: 'error',
        error: getErrorMessage(error, "Couldn't load the settings."),
      });
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const groups = useMemo(() => {
    const map = new Map();
    data.sections.forEach(section => {
      if (!map.has(section.group)) map.set(section.group, []);
      map.get(section.group).push(section);
    });
    return [...map.entries()];
  }, [data.sections]);

  const requested = params.get('section');
  const active =
    requested === HISTORY || data.sections.some(s => s.id === requested)
      ? requested
      : data.sections[0]?.id;
  const section = data.sections.find(s => s.id === active);
  const draft = drafts[active] || {};
  const pendingResets = resets[active] || [];
  const dirty = hasEntries(draft) || pendingResets.length > 0;
  const anyDirty =
    Object.values(drafts).some(hasEntries) ||
    Object.values(resets).some(r => r.length > 0);

  // Leaving the page with unsaved edits asks first (switching sections keeps them).
  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  const open = id => {
    setParams(id ? { section: id } : {}, { replace: false });
    setErrors({});
    mainRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const change = (field, value) => {
    setDrafts(prev => {
      const next = { ...(prev[active] || {}) };
      if (field.kind !== 'Secret' && sameAsCurrent(field, value))
        delete next[field.key];
      else next[field.key] = value;
      return { ...prev, [active]: next };
    });
    setResets(prev => ({
      ...prev,
      [active]: (prev[active] || []).filter(k => k !== field.key),
    }));
    setErrors(prev => {
      if (!prev[field.key]) return prev;
      const next = { ...prev };
      delete next[field.key];
      return next;
    });
  };

  const discardField = field =>
    setDrafts(prev => {
      const next = { ...(prev[active] || {}) };
      delete next[field.key];
      return { ...prev, [active]: next };
    });

  const resetField = (field, undo = false) => {
    setResets(prev => {
      const list = (prev[active] || []).filter(k => k !== field.key);
      return { ...prev, [active]: undo ? list : [...list, field.key] };
    });
    if (!undo) discardField(field);
  };

  const discardAll = () => {
    setDrafts(prev => ({ ...prev, [active]: {} }));
    setResets(prev => ({ ...prev, [active]: [] }));
    setErrors({});
  };

  const save = async event => {
    event.preventDefault();
    if (!section || !dirty || saving) return;

    const found = {};
    Object.entries(draft).forEach(([key, value]) => {
      const field = section.fields.find(f => f.key === key);
      const error = field ? validateField(field, value) : null;
      if (error) found[key] = error;
    });
    if (hasEntries(found)) {
      setErrors(found);
      focusFirstError(section, found);
      return;
    }

    const values = Object.fromEntries(
      Object.entries(draft).map(([key, value]) => [
        key,
        toPayload(
          section.fields.find(f => f.key === key),
          value
        ),
      ])
    );

    setSaving(true);
    try {
      const response = await settingsAPI.save(
        section.id,
        values,
        pendingResets
      );
      const saved = response.data?.data;
      setData(prev => ({
        sections: prev.sections.map(s =>
          s.id === saved.section.id ? saved.section : s
        ),
        status: saved.status,
        history: saved.change
          ? [saved.change, ...prev.history].slice(0, 30)
          : prev.history,
      }));
      setDrafts(prev => ({ ...prev, [section.id]: {} }));
      setResets(prev => ({ ...prev, [section.id]: [] }));
      setErrors({});
      toast.success(
        saved.change
          ? 'Saved. The new settings are in effect now.'
          : 'Nothing changed.'
      );
      if (SITE_SECTIONS.includes(section.id)) refreshSiteConfig();
    } catch (error) {
      const fieldErrors =
        error?.response?.status === 400 ? error.response.data?.data : null;
      if (
        fieldErrors &&
        typeof fieldErrors === 'object' &&
        hasEntries(fieldErrors)
      ) {
        setErrors(fieldErrors);
        focusFirstError(section, fieldErrors);
        toast.error('Some settings need fixing.');
      } else {
        toast.error(
          getErrorMessage(error, "Couldn't save the settings. Try again.")
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (load.status === 'loading') {
    return (
      <div>
        <PageHeader title="Settings" />
        <LoadingState label="Loading settings" />
      </div>
    );
  }

  if (load.status === 'error') {
    return (
      <div>
        <PageHeader title="Settings" />
        <ErrorState
          title="Couldn't load the settings"
          message={load.error}
          onRetry={fetchSettings}
        />
      </div>
    );
  }

  const reload = () => {
    setDrafts({});
    setResets({});
    setErrors({});
    fetchSettings();
  };

  const reloadButton = (
    <button
      type="button"
      className="pk-btn pk-btn--secondary pk-btn--sm"
      onClick={anyDirty ? undefined : reload}
    >
      <RefreshCw aria-hidden="true" />
      Reload
    </button>
  );

  const otherErrors = section
    ? Object.entries(errors).filter(
        ([key]) => !section.fields.some(f => f.key === key)
      )
    : [];

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Payments, checkout, invoices, email and your website's details. Saved changes apply right away, with no restart."
        actions={
          anyDirty ? (
            <Popconfirm
              title="Discard unsaved changes?"
              description="Reloading loses the edits you haven't saved."
              okText="Discard and reload"
              okDanger
              placement="bottom"
              onConfirm={reload}
            >
              {reloadButton}
            </Popconfirm>
          ) : (
            reloadButton
          )
        }
      />

      {data.status?.loadError && (
        <div
          className="pk-alert adm-set__banner"
          data-tone="warning"
          role="alert"
        >
          <TriangleAlert aria-hidden="true" />
          <span>
            <strong>Saved settings couldn't be read from the database.</strong>{' '}
            The last values that loaded are still in effect.{' '}
            {data.status.loadError}
          </span>
        </div>
      )}

      <div className="adm-set">
        <div className="adm-set__menu">
          <label className="sr-only" htmlFor="set-section-select">
            Settings section
          </label>
          <select
            id="set-section-select"
            className="pk-select adm-set__select"
            value={active}
            onChange={e => open(e.target.value)}
          >
            {groups.map(([group, sections]) => (
              <optgroup key={group} label={group}>
                {sections.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </optgroup>
            ))}
            <option value={HISTORY}>Change history</option>
          </select>
          <SettingsNav
            groups={groups}
            active={active}
            status={data.status}
            drafts={drafts}
            resets={resets}
            onSelect={open}
          />
        </div>

        <div className="adm-set__main" ref={mainRef}>
          {active === HISTORY && (
            <ChangeHistory
              history={data.history}
              sections={data.sections}
              onOpen={open}
            />
          )}

          {section && (
            <form
              className="adm-panel adm-set__panel"
              onSubmit={save}
              noValidate
              aria-labelledby="set-section-title"
            >
              <div className="adm-panel__head adm-set__head">
                <div>
                  <h2 className="adm-panel__title" id="set-section-title">
                    {section.title}
                  </h2>
                  <p className="adm-set__desc">{section.description}</p>
                </div>
                {section.lastChangedAt && (
                  <p className="adm-set__changed">
                    Changed {formatDate(section.lastChangedAt, 'datetime')}
                    {section.lastChangedBy
                      ? ` by ${section.lastChangedBy}`
                      : ''}
                  </p>
                )}
              </div>

              <div className="adm-set__body">
                {PAYMENT_SECTIONS.includes(section.id) && (
                  <CheckoutStrip
                    methods={data.status?.paymentMethods}
                    onOpen={open}
                  />
                )}
                {section.id === 'invoice' && data.status?.invoiceProblem && (
                  <div
                    className="pk-alert adm-set-strip"
                    data-tone="warning"
                    role="status"
                  >
                    <TriangleAlert aria-hidden="true" />
                    <span>
                      <strong>Invoices can't be issued yet:</strong>{' '}
                      {data.status.invoiceProblem}
                    </span>
                  </div>
                )}

                {otherErrors.length > 0 && (
                  <div
                    className="pk-alert adm-set-strip"
                    data-tone="danger"
                    role="alert"
                  >
                    <TriangleAlert aria-hidden="true" />
                    <span>
                      {otherErrors.map(([, message]) => message).join(' ')}
                    </span>
                  </div>
                )}

                <div className="adm-set__fields">
                  {section.fields.map(field => {
                    const hasDraft = Object.prototype.hasOwnProperty.call(
                      draft,
                      field.key
                    );
                    return (
                      <SettingControl
                        key={field.key}
                        field={field}
                        value={hasDraft ? draft[field.key] : field.value}
                        hasDraft={hasDraft}
                        error={errors[field.key]}
                        resetPending={pendingResets.includes(field.key)}
                        onChange={value => change(field, value)}
                        onDiscard={() => discardField(field)}
                        onReset={() => resetField(field)}
                        onUndoReset={() => resetField(field, true)}
                      />
                    );
                  })}
                </div>

                <SectionExtra section={section} draft={draft} dirty={dirty} />
              </div>

              <div className={cx('adm-set__savebar', dirty && 'is-dirty')}>
                <span className="adm-set__savebar-text" aria-live="polite">
                  {dirty ? (
                    'Unsaved changes'
                  ) : (
                    <>
                      <Info aria-hidden="true" />
                      Changes apply as soon as you save.
                    </>
                  )}
                </span>
                <div className="adm-set__savebar-actions">
                  <button
                    type="button"
                    className="pk-btn pk-btn--ghost pk-btn--sm"
                    onClick={discardAll}
                    disabled={!dirty || saving}
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="pk-btn pk-btn--primary pk-btn--sm"
                    disabled={!dirty || saving}
                    aria-busy={saving || undefined}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/** Moves focus to the first field with an error, in on-screen order. */
function focusFirstError(section, found) {
  const first = section.fields.find(f => found[f.key]);
  if (!first) return;
  const el = document.getElementById(
    `set-${first.key.replace(/[^A-Za-z0-9]+/g, '-')}`
  );
  el?.focus?.();
}

export default Settings;
